import Image from 'next/image'
import { FunctionComponent } from 'react'
import styled, { css, FlattenSimpleInterpolation } from 'styled-components'
import adIdentityImage from '@/assets/images/find-kol-image.webp'
import kolIdentityImage from '@/assets/images/kol-image.webp'
import { H3, H4, StyleParagraph } from '@/components/common/typography'
import { useBreakpoint } from '@/hooks/use-breakpoint'
import { useIntl } from '@/i18n/hooks/use-intl'

const PC_IDENTITY_IMAGE_SIZE = 160
const MOBILE_IDENTITY_IMAGE_SIZE = 100
const MIN_OPTION_SIZE = 288

type IdentityOptionProps = {
  image: {
    src: string
    alt: string
  }
  title: string
  description: string
  onClick?: () => void
}

const IdentityOption: FunctionComponent<IdentityOptionProps> = ({
  image,
  title,
  description,
  onClick,
}) => {
  const braekpoint = useBreakpoint()
  const isPC = braekpoint === 'PC'

  return (
    <Container
      $isClickable={typeof onClick !== 'undefined'}
      $isPC={isPC}
      onClick={onClick}
    >
      <Image
        alt={image.alt}
        height={isPC ? PC_IDENTITY_IMAGE_SIZE : MOBILE_IDENTITY_IMAGE_SIZE}
        src={image.src}
        width={isPC ? PC_IDENTITY_IMAGE_SIZE : MOBILE_IDENTITY_IMAGE_SIZE}
      />
      <ContentContainer $isPC={isPC}>
        {isPC ? (
          <H3 style={{ marginBottom: 0 }}>{title}</H3>
        ) : (
          <H4 style={{ marginBottom: 0 }}>{title}</H4>
        )}
        <StyleParagraph style={{ lineHeight: 1.4 }}>
          {description}
        </StyleParagraph>
      </ContentContainer>
    </Container>
  )
}

type RegisterIdentityOptionProps = Pick<IdentityOptionProps, 'onClick'>

const RegisterIdentityAdOption: FunctionComponent<
  RegisterIdentityOptionProps
> = ({ onClick }) => {
  const { formatMessage } = useIntl()

  return (
    <IdentityOption
      description={formatMessage({ id: 'account:ad_plan_brief' })}
      image={{ src: adIdentityImage.src, alt: 'AD identity option' }}
      title={formatMessage({ id: 'account:ad_plan_title' })}
      onClick={onClick}
    />
  )
}

const RegisterIdentityKolOption: FunctionComponent<
  RegisterIdentityOptionProps
> = ({ onClick }) => {
  const { formatMessage } = useIntl()

  return (
    <IdentityOption
      description={formatMessage({ id: 'account:kol_plan_brief' })}
      image={{ src: kolIdentityImage.src, alt: 'KOL identity option' }}
      title={formatMessage({ id: 'account:kol_plan_title' })}
      onClick={onClick}
    />
  )
}

const PCContainerStyle = css`
  flex-direction: column;
  gap: 16px;
  padding: 24px;
`

const MobileContainerStyle = css`
  flex-direction: row;
  gap: 24px;
  padding: 24px 32px;
`

const Container = styled.div<{ $isPC: boolean; $isClickable?: boolean }>`
  display: flex;
  align-items: center;
  background-color: ${(props): string => props.theme.colors.base.grey.grey7};
  border: 1px solid ${(props): string => props.theme.colors.base.grey.grey5};
  border-radius: 8px;
  min-width: ${MIN_OPTION_SIZE}px;
  cursor: ${({ $isClickable }): string =>
    $isClickable ? 'pointer' : 'default'};

  ${({ $isPC }): FlattenSimpleInterpolation =>
    $isPC ? PCContainerStyle : MobileContainerStyle}
`

const PCContentContainerStyle = css`
  justify-content: center;
  align-items: center;
`

const MobileContentContainerStyle = css`
  justify-content: center;
  align-items: start;
`

const ContentContainer = styled.div<{ $isPC: boolean }>`
  display: flex;
  flex-direction: column;

  ${({ $isPC }): FlattenSimpleInterpolation =>
    $isPC ? PCContentContainerStyle : MobileContentContainerStyle}
`

export { RegisterIdentityAdOption, RegisterIdentityKolOption }
